import React, { useRef, useState } from "react";
import { api, formatApiErrorDetail, fileUrl } from "@/lib/api";
import { UploadCloud, Loader2, FileText, X, Paperclip } from "lucide-react";
import { toast } from "sonner";

// Uploads a single file, returns {file_id, filename}. Controlled list via value/onChange.
export function FileUploader({ files, onChange, testId = "file-uploader", multiple = true }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handle = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setUploading(true);
    const added = [];
    for (const f of list) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push({ file_id: data.file_id, filename: data.filename });
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Gagal upload");
      }
    }
    onChange(multiple ? [...files, ...added] : added.slice(0, 1));
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = (id) => onChange(files.filter((f) => f.file_id !== id));

  return (
    <div data-testid={testId}>
      <input ref={inputRef} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" multiple={multiple}
        onChange={handle} className="hidden" data-testid={`${testId}-input`} />
      <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
        data-testid={`${testId}-btn`}
        className="w-full border-2 border-dashed border-border rounded-xl px-4 py-6 flex flex-col items-center justify-center gap-2 hover:border-brand hover:bg-secondary/40 transition-colors">
        {uploading ? <Loader2 className="h-6 w-6 animate-spin text-brand" /> : <UploadCloud className="h-6 w-6 text-brand" />}
        <span className="text-sm font-medium">{uploading ? "Mengunggah..." : "Klik untuk upload bukti"}</span>
        <span className="text-xs text-muted-foreground">JPG, PNG, atau PDF (maks. 10MB)</span>
      </button>
      {files.length > 0 && (
        <ul className="mt-3 space-y-2">
          {files.map((f) => (
            <li key={f.file_id} className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg border bg-card">
              <a href={fileUrl(f.file_id)} target="_blank" rel="noreferrer"
                className="flex items-center gap-2 text-sm text-brand hover:underline min-w-0">
                <FileText className="h-4 w-4 shrink-0" /> <span className="truncate">{f.filename}</span>
              </a>
              <button type="button" onClick={() => remove(f.file_id)} className="text-muted-foreground hover:text-red-600">
                <X className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
